import { Injectable, inject } from '@angular/core';

import { DateService } from '../date/date.service';
import { DateCodecService } from '../date/date-codec.service';
import { RestJsonCodecService } from './rest-json-codec.service';
import type { RestParameterDescriptor, RestResourceDescriptor } from './rest.contract';
import type { RestParams } from './rest-request';

@Injectable({
  providedIn: 'root'
})
export class RestInterpolatorService {
  private readonly dateCodecService = inject<DateCodecService>(DateCodecService);
  private readonly restJsonCodecService = inject<RestJsonCodecService>(RestJsonCodecService);

  /**
   * Replaces placeholders in the url bases on RestResourceDescriptor data and parameters
   *
   * @returns rest endpoint url with base url appended.
   */
  interpolate(resourceDescriptor: RestResourceDescriptor, baseUrl: string, params: RestParams): string | never {
    const url: string = baseUrl + resourceDescriptor.href;
    const interpolatedUrl = url.replace(
      /{([a-z]\w*)}/gi,
      (_$0: string, label: string): string => this.getPlaceholderValue(resourceDescriptor, params, label));

    const deduped = interpolatedUrl.replace(/(^|[^:])\/{2,}/g, '$1/');
    let end = deduped.length - 1;
    while (end >= 0 && deduped.charCodeAt(end) === 47 /* '/' */) {
      end--;
    }
    return deduped.slice(0, end + 1);
  }

  formatParameter(value: unknown): string {
    if (value !== undefined && value !== null) {
      if (typeof value === 'string') {
        return value;
      }
      if (DateService.isDate(value)) {
        return this.dateCodecService.encode(value as Date) ?? '';
      }
      if (typeof value === 'number') {
        return value.toString();
      }
      return this.restJsonCodecService.encode(value) ?? '';
    }
    return value as string;
  }

  private getPlaceholderValue(resourceDescriptor: RestResourceDescriptor, params: RestParams | undefined, key: string): string | never {
    const value = params?.[key];

    if (value !== null && value !== undefined) {
      return this.encodeParameterValue(this.formatParameter(value));
    }

    if (!this.isRequiredPathVariable(resourceDescriptor, key)) {
      return '';
    }

    throw new Error('Required url parameter is missing: ' + key);
  }

  private isRequiredPathVariable(resourceDescriptor: RestResourceDescriptor, key: string): boolean {
    const pathVariableDescriptor = resourceDescriptor.pathVariables
      ?.find((descriptor: RestParameterDescriptor): boolean => descriptor.name === key);

    if (pathVariableDescriptor === undefined) {
      return true;
    }

    return pathVariableDescriptor.required === true;
  }

  private encodeParameterValue(value: string): string { // TODO this is needed because IE11 of course. Remove when deprecated
    return encodeURIComponent(value)
      .replace(/%3A/gi, ':')
      .replace(/%24/gi, '$')
      .replace(/%2C/gi, ',')
      .replace(/%3B/gi, ';')
      .replace(/%2B/gi, '+')
      .replace(/%3D/gi, '=')
      .replace(/%3F/gi, '?')
      .replace(/%40/gi, '@')
      .replace(/%2F/gi, '/');
  }

}
